import { RotateCcw } from "lucide-react";
import type { BillingCycle } from "../types/subscription";
import { CURRENCIES } from "../lib/currencies";
import {
  DEFAULT_USER_SETTINGS,
  type ThemePreference,
} from "../lib/user-settings";
import { useUserSettings } from "../lib/settings-context";
import { cn } from "../lib/cn";
import { CategoryField } from "./CategoryField";

const THEME_OPTIONS: { value: ThemePreference; label: string; hint: string }[] =
  [
    { value: "system", label: "System", hint: "Match OS light or dark mode" },
    { value: "light", label: "Light", hint: "Always light appearance" },
    { value: "dark", label: "Dark", hint: "Always dark appearance" },
  ];

const LOCALE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Browser default" },
  { value: "en-US", label: "English (United States)" },
  { value: "en-GB", label: "English (United Kingdom)" },
  { value: "en-CA", label: "English (Canada)" },
  { value: "en-AU", label: "English (Australia)" },
  { value: "de-DE", label: "German (Germany)" },
  { value: "fr-FR", label: "French (France)" },
  { value: "es-ES", label: "Spanish (Spain)" },
  { value: "es-MX", label: "Spanish (Mexico)" },
  { value: "it-IT", label: "Italian (Italy)" },
  { value: "pt-BR", label: "Portuguese (Brazil)" },
  { value: "pt-PT", label: "Portuguese (Portugal)" },
  { value: "nl-NL", label: "Dutch (Netherlands)" },
  { value: "pl-PL", label: "Polish (Poland)" },
  { value: "sv-SE", label: "Swedish (Sweden)" },
  { value: "nb-NO", label: "Norwegian Bokmål" },
  { value: "da-DK", label: "Danish (Denmark)" },
  { value: "fi-FI", label: "Finnish (Finland)" },
  { value: "ja-JP", label: "Japanese (Japan)" },
  { value: "ko-KR", label: "Korean (South Korea)" },
  { value: "zh-CN", label: "Chinese (China)" },
  { value: "zh-TW", label: "Chinese (Taiwan)" },
  { value: "hi-IN", label: "Hindi (India)" },
  { value: "id-ID", label: "Indonesian" },
  { value: "th-TH", label: "Thai (Thailand)" },
  { value: "vi-VN", label: "Vietnamese" },
];

const BILLING_PRESETS: { value: BillingCycle; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom_days", label: "Custom period (days)" },
];

const RENEWAL_WINDOWS: (30 | 60 | 90)[] = [30, 60, 90];

const fieldClass =
  "mt-1 w-full rounded-lg border border-border bg-surface-1 px-3 py-2 text-sm text-text focus:border-accent focus:ring-1 focus:ring-accent/40";

const helpClass = "mt-1 text-xs text-text-muted";

function Section(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface-1/80 p-5 backdrop-blur-sm">
      <h2 className="font-display text-base font-semibold text-text">
        {props.title}
      </h2>
      {props.description && (
        <p className="mt-1 text-sm text-pretty text-text-muted">{props.description}</p>
      )}
      <div className="mt-4 space-y-4">{props.children}</div>
    </section>
  );
}

export function SettingsPage() {
  const { settings, patchSettings, setSettings } = useUserSettings();

  return (
    <div className="space-y-6">
      <Section
        title="Appearance"
        description="Choose how Dripwatch looks on this device. System follows your OS setting."
      >
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Theme
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {THEME_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => patchSettings({ theme: o.value })}
                title={o.hint}
                className={cn(
                  "rounded-xl border px-3 py-2 text-left text-sm transition-colors",
                  settings.theme === o.value
                    ? "border-accent bg-surface-2 font-medium text-text shadow-sm"
                    : "border-border text-text-muted hover:border-border hover:bg-surface-2/60 hover:text-text",
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section
        title="Regional"
        description="Default currency and how amounts are formatted in lists and charts."
      >
        <div>
          <label
            htmlFor="set-currency"
            className="text-xs font-medium uppercase tracking-wide text-text-muted"
          >
            Default currency
          </label>
          <p className={helpClass}>
            Prefills the currency when you add a subscription. Each plan keeps the
            currency you choose on its form.
          </p>
          <select
            id="set-currency"
            className={cn(fieldClass, "cursor-pointer appearance-none bg-surface-1")}
            value={settings.defaultCurrency}
            onChange={(e) => patchSettings({ defaultCurrency: e.target.value })}
          >
            {CURRENCIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="set-locale"
            className="text-xs font-medium uppercase tracking-wide text-text-muted"
          >
            Number formatting
          </label>
          <p className={helpClass}>
            Symbol position, thousands separators, and decimals for currency display.
          </p>
          <select
            id="set-locale"
            className={cn(fieldClass, "cursor-pointer appearance-none bg-surface-1")}
            value={settings.numberLocale}
            onChange={(e) => patchSettings({ numberLocale: e.target.value })}
          >
            {LOCALE_OPTIONS.map((o) => (
              <option key={o.value || "sys"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </Section>

      <Section
        title="New subscriptions"
        description="Starting values when you open “Add subscription”. You can change them per plan."
      >
        <div>
          <label
            htmlFor="set-billing"
            className="text-xs font-medium uppercase tracking-wide text-text-muted"
          >
            Default billing cycle
          </label>
          <select
            id="set-billing"
            className={cn(fieldClass, "cursor-pointer appearance-none bg-surface-1")}
            value={settings.defaultBillingCycle}
            onChange={(e) =>
              patchSettings({ defaultBillingCycle: e.target.value as BillingCycle })
            }
          >
            {BILLING_PRESETS.map((b) => (
              <option key={b.value} value={b.value}>
                {b.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="set-category"
            className="text-xs font-medium uppercase tracking-wide text-text-muted"
          >
            Default category
          </label>
          <CategoryField
            id="set-category"
            value={settings.defaultCategory}
            onChange={(v) => patchSettings({ defaultCategory: v })}
            hint="Starting category when you add a plan. Quick picks match the subscription form."
          />
        </div>
      </Section>

      <Section
        title="Dashboard"
        description="Renewal window buttons on the overview (30 / 60 / 90 days)."
      >
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Default renewal window
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {RENEWAL_WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => patchSettings({ dashboardDefaultRenewalWindow: w })}
                className={cn(
                  "rounded-xl border px-3 py-1.5 text-sm font-medium transition-colors",
                  settings.dashboardDefaultRenewalWindow === w
                    ? "border-accent bg-surface-2 text-text"
                    : "border-border text-text-muted hover:bg-surface-2/60 hover:text-text",
                )}
              >
                {w} days
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section
        title="Subscription list"
        description="How the full list behaves on the Subscriptions page."
      >
        <label className="flex cursor-pointer items-start gap-3 text-sm text-text">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-border bg-surface-0 text-accent focus:ring-accent"
            checked={settings.subscriptionListShowInactive}
            onChange={(e) =>
              patchSettings({ subscriptionListShowInactive: e.target.checked })
            }
          />
          <span>
            <span className="font-medium">Show inactive plans</span>
            <span className="mt-0.5 block text-text-muted">
              Turn off to hide paused or canceled items from the table. They stay in your
              data.
            </span>
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-3 text-sm text-text">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-border bg-surface-0 text-accent focus:ring-accent"
            checked={settings.confirmBeforeDelete}
            onChange={(e) =>
              patchSettings({ confirmBeforeDelete: e.target.checked })
            }
          />
          <span>
            <span className="font-medium">Confirm before delete</span>
            <span className="mt-0.5 block text-text-muted">
              Ask for confirmation when removing a subscription from the list.
            </span>
          </span>
        </label>
      </Section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setSettings({ ...DEFAULT_USER_SETTINGS })}
          className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-medium text-text-muted hover:bg-surface-2 hover:text-text"
        >
          <RotateCcw className="h-4 w-4" />
          Reset all settings
        </button>
      </div>
    </div>
  );
}
